<template>
  <div class="studio-lesson-content" v-bind="$attrs" v-on="$listeners">
    <div
      class="d-flex flex-row align-items-top justify-content-between flex-wrap flex-lg-nowrap"
    >
      <h2 class="m-0 mr-lg-5">{{ lesson.lesson_name }}</h2>
      <div class="d-flex mt-3 mt-lg-0">
        <div v-if="isEnabled">
          <HMButton
            v-if="!lessonCompleted"
            :disabled="
              lesson.pass_quiz_to_progress && lesson.quiz_status !== 'passed'
            "
            color="brand"
            @click="completeLesson"
          >
            <span class="small text-nowrap">
              {{ $t('PRODUCTS.BUTTON.MARK_AS_COMPLETE') }}
            </span>
          </HMButton>
          <HMButton v-else color="brand-opacity-01" @click="incompleteLesson">
            <span class="small text-nowrap">
              {{ $t('PRODUCTS.BUTTON.COMPLETED') }}
              <i class="fa fa-check ml-2"></i>
            </span>
          </HMButton>
        </div>
      </div>
    </div>
    <HMCustomContent
      class="mt-4"
      :html="lesson.lesson_description"
      :json="lesson.lesson_description_new"
    />
    <HMCustomContent
      class="mt-4"
      v-if="hasAccess && !lesson.locked && lesson.lesson_text"
      :json="lesson.lesson_text"
    />
    <div class="row mt-4">
      <div
        class="studio-lesson-media-card--container col-12 mb-4"
        v-for="media in lesson.content"
        :key="media.id"
      >
        <router-link
          class="undecorated-router-link"
          :is="hasAccessButLessonLocked ? 'span' : 'router-link'"
          :to="getCorrectRoute({ lesson_id: lesson.id, media_id: media.id })"
        >
          <StudioLessonMediaCard :media="media" :hasAccess="isEnabled" />
        </router-link>
      </div>
    </div>
    <div v-if="!lesson.locked && lesson.quiz" class="mt-4">
      <StudioLessonQuizCard
        class="my-4"
        :status="lesson.quiz_status"
        :retakeAllowed="lesson.allow_retaking_quiz"
        @startQuiz="showQuizInModal"
        @retakeQuiz="showQuizInModal"
      />
    </div>
  </div>
</template>

<script>
import StudioLessonMediaCard from '@/web/components/products/StudioLessonMediaCard';
import StudioLessonQuizCard from '@/web/components/products/StudioLessonQuizCard';
import HMToggleButtonGroup from '@/web/components/common/HMToggleButtonGroup';
import HMCustomContent from '@/web/components/common/HMCustomContent';
import QuizModal from '@/web/components/quizzes/QuizModal';
import { getStudioURL } from '@/web/router/custom-domain-utils';
import { errorToMessage } from '@/core/utils/errors';
import { getLessonType } from '@/core/utils/products';
import LessonType from '@/core/typings/enums/LessonType';

export default {
  name: 'StudioLessonContent',
  components: {
    HMCustomContent,
    HMToggleButtonGroup,
    StudioLessonMediaCard,
    StudioLessonQuizCard,
  },
  props: {
    product: {
      type: Object,
    },
    lesson: {
      type: Object,
    },
    studioURL: {
      type: String,
    },
    hasAccess: {
      type: Boolean,
    },
    hasPurchased: {
      type: Boolean,
    },
  },
  data() {
    return {
      lessonCompleted: false,
    };
  },
  computed: {
    isEnabled() {
      return !this.lesson.locked && this.hasAccess;
    },
    hasAccessButLessonLocked() {
      return this.lesson.locked && (this.hasAccess || this.hasPurchased);
    },
  },
  methods: {
    lessonCompleteToggled() {
      if (this.lessonCompleted) {
        this.completeLesson();
      } else {
        this.incompleteLesson();
      }
    },
    async completeLesson() {
      try {
        await this.$store.dispatch('product/checkLessonAsCompleted', {
          studioURL: this.studioURL,
          productId: this.lesson.product,
          lessonId: this.lesson.id,
        });
        this.lessonCompleted = true;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: `Failed to mark lesson completed`,
          duration: -1, // stay ON
        });
      }
    },
    async incompleteLesson() {
      try {
        await this.$store.dispatch('product/checkLessonAsIncompleted', {
          studioURL: this.studioURL,
          productId: this.lesson.product,
          lessonId: this.lesson.id,
        });
        this.lessonCompleted = false;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: `Failed to mark lesson completed`,
          duration: -1, // stay ON
        });
      }
    },
    getCorrectRoute({ lesson_id, media_id }) {
      if (!this.isEnabled) {
        return {
          name: 'studio-product-checkout',
          params: { product_id: this.$route.params.product_id },
        };
      }
      return {
        name: 'studio-lesson-media',
        params: { lesson_id, media_id },
      };
    },
    updateLessonQuizStatus(solvedQuiz) {
      this.$store.commit('product/UPDATE_LESSON_QUIZ_STATUS', {
        lesson_id: this.lesson.id,
        status: solvedQuiz.status,
      });
    },
    async showQuizInModal() {
      const studioURL = getStudioURL(this.$route);
      const quizId = this.lesson.quiz;
      try {
        const quiz = await this.$store.dispatch('studentSolvedQuiz/getQuiz', {
          studioURL,
          quizId,
        });
        this.$modal.show(
          QuizModal,
          {
            studioURL,
            quizId,
            quiz,
          },
          { classes: ['vm--inline-modal'], clickToClose: false },
          {
            finish: this.updateLessonQuizStatus,
          }
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: `Failed to load quiz ${quizId}`,
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    redirectToLessonDetail(lesson) {
      const lessonType = getLessonType(lesson);
      if (lessonType === LessonType.TEXT || lessonType === LessonType.QUIZ) {
        this.$router.replace({
          name: 'studio-lesson-non-media',
          params: {
            product_id: this.$route.params.product_id,
            lesson_id: this.lesson?.id,
          },
        });
      } else if (lessonType === LessonType.MEDIA) {
        this.$router.replace({
          name: 'studio-lesson-media',
          params: {
            product_id: this.$route.params.product_id,
            lesson_id: this.lesson?.id,
            media_id: this.lesson?.content[0]?.id,
          },
        });
      }
    },
  },
  watch: {
    lesson: {
      handler(lesson) {
        this.lessonCompleted = lesson?.completed;
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.undecorated-router-link {
  cursor: pointer;
}
.studio-lesson-media-card--container:last-child {
  margin-bottom: 0 !important;
}
.review {
  pointer-events: none;
}
</style>
